import React, {
  createContext,
  useContext,
  useState,
  ReactNode,
  useEffect,
  useRef,
} from "react";
import { authHelper } from "../utils/helpers";
import { jwtDecode } from "jwt-decode";
import {
  fetchLogoutProxy,
  fetchSessionProxy,
  fetchSessionValidateProxy,
  fetchUserInfo,
} from "../utils/helpers/api";
import { Http, Routes } from "../utils/constants";
import { fetchGetNewToken, fetchGetNewTokenSpa } from "../utils/helpers/auth0";
import { matchPath, useNavigate } from "react-router-dom";
import routes from "../utils/constants/routes";
import { logger } from "../utils/helpers/logger";
import { attachBulkDownloadIfNeeded } from "../utils/helpers/function";

interface GlobalStateType {
  loader: boolean;
  isShowMenu: boolean;
  isAuthenticated: boolean;
  breadcrumbs: IBreadcrumb[] | null;
  user?: any;
  modalContext?: CommonModalProps;
  isSaving?: boolean; // 保存処理中フラグ
}
interface IBreadcrumb {
  path: string | null;
  label: string;
}

interface GlobalContextType {
  globalState: GlobalStateType;
  setGlobalState: (globalState: GlobalStateType) => void;
  setStateValue: (
    key: string,
    value: string | boolean | IBreadcrumb[] | null
  ) => void;
  setModal: (modalContext: CommonModalProps) => void;
  setModalDisabledConfirm?: (value: boolean) => void;
  clearStateValue: (key: string) => void;
  refreshAccessToken: () => Promise<void>;
  createExternalSession: () => Promise<string | null>;
  fetchAndSetUserInfo: () => Promise<any | null>;
  handleUserRedirect: (userData: any) => Promise<void>;
}

const GlobalContext = createContext<GlobalContextType | undefined>(undefined);

export const GlobalProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  // refreshAccessTokenの処理が終わらずに再実行するとGoogleログインユーザーの場合は403エラーになるため、それを防止する
  const refreshPromise = useRef<Promise<void> | null>(null);
  const [globalState, setGlobalState] = useState<GlobalStateType>({
    loader: false,
    isShowMenu: false,
    isAuthenticated: false,
    breadcrumbs: null,
    isSaving: false,
    modalContext: {
      isOpen: false,
      title: "",
      childrenTitle: null,
      childrenSession: null,
      onClose: undefined,
      onConfirm: undefined,
      confirmText: "Confirm",
      cancelText: "Cancel",
      showButton: false,
      disabledConfirm: false,
      hideConfirmButton: false,
    },
  });
  const [accessToken, setAccessToken] = useState("");
  const navigate = useNavigate();

  useEffect(() => {
    const interval = setInterval(() => {
      const token = authHelper.accessToken();
      setAccessToken(token ?? "");
    }, 100);
    setTimeout(() => {
      clearInterval(interval);
    }, 5000);
    return () => clearInterval(interval);
  }, [location.pathname]);

  useEffect(() => {
    if (accessToken) {
      const decode = jwtDecode(accessToken);
      const currentPath = [
        Routes.OPTOUTNOTIFICATION,
      ];

      const notFetchPath= currentPath.some((path) =>
        matchPath(path, location.pathname)
      );
      if(!notFetchPath) {
        handleFetchUserInfo(decode.sub ?? "");
        setGlobalState((prevState) => ({
          ...prevState,
          isAuthenticated: true,
        }));
      }
    } else {
      setGlobalState((prevState) => ({
        ...prevState,
        isAuthenticated: false,
      }));
    }
  }, [accessToken]);

  const refreshAccessToken = async (): Promise<void> => {
    if (refreshPromise.current) {
      return refreshPromise.current;
    }

    refreshPromise.current = (async () => {
      const isGoogleLogin = localStorage.getItem("isGoogleLogin");
      try {
        const refresh_token = authHelper.valueCookie("_rftk");
        const response = isGoogleLogin
          ? await fetchGetNewTokenSpa(refresh_token)
          : await fetchGetNewToken(refresh_token);
        if (response) {
          const { access_token, expires_in, refresh_token: new_refresh_token } = response;
          setAccessToken(access_token);
          authHelper.setAccessToken(access_token, expires_in);
          setGlobalState((prevState) => ({
            ...prevState,
            isAuthenticated: true,
          }));
          if (isGoogleLogin) {
            authHelper.setValueToCookie("_rftk", new_refresh_token, "31536000");
          } else {
            await fetchSessionProxy({
              access_token,
              expires_in,
              refresh_token,
            });
          }
        }
      } catch (err) {
        try {
          await fetchLogoutProxy();
        } finally {
          authHelper.logOut();
        }
      } finally {
        setTimeout(() => {
          refreshPromise.current = null;
        }, 1000)
      }
    })()
    return refreshPromise.current
  };

  // 外部ドメイン用のセッション作成
  const createExternalSession = async (): Promise<string | null> => {
    const isGoogleLogin = localStorage.getItem("isGoogleLogin");
    try {
      const refresh_token = authHelper.valueCookie("_rftk");
      const response = isGoogleLogin
        ? await fetchGetNewTokenSpa(refresh_token)
        : await fetchGetNewToken(refresh_token);
      
      if (!response) return null;
      
      const { access_token, expires_in, refresh_token: new_refresh_token } = response;
      
      // Google ログインの場合は新しい refresh_token を保存
      if (isGoogleLogin && new_refresh_token) {
        authHelper.setValueToCookie("_rftk", new_refresh_token, "31536000");
      }
      
      const result = await fetchSessionProxy({
        access_token,
        expires_in,
        refresh_token: new_refresh_token || refresh_token,
        external: true,
      });
      
      return result?.data?.session_id || null;
    } catch (err) {
      return null;
    }
  };

  // ユーザー情報を取得してglobalStateに設定する（純粋な取得処理）
  const fetchAndSetUserInfo = async (): Promise<any | null> => {
    try {
      const isLogin = localStorage.getItem("isLogin");
      if (!isLogin && location.pathname != routes.LOGIN) {
        await fetchSessionValidateProxy();
      }
      const result = await fetchUserInfo();
      if (result?.status === Http.SUCCESS_CODE) {
        setGlobalState((prevState) => {
          // タイムスタンプチェック：古いデータで上書きしない
          const existingUpdatedAt = prevState.user?.updated_at
            ? new Date(prevState.user.updated_at).getTime()
            : 0;
          const newUpdatedAt = new Date(result.data.userdata.updated_at).getTime();
          
          if (existingUpdatedAt >= newUpdatedAt) {
            return prevState;
          }
          
          return {
            ...prevState,
            user: result.data.userdata,
          };
        });
        return result.data.userdata;
      }
      return null;
    } catch (err) {
      throw err;
    }
  };

  // リダイレクト処理を行う
  const handleUserRedirect = async (userData: any) => {
    logger("[GlobalContext]", "Handling user redirect with userData:", userData, location.pathname);
    const user_status = userData.user_metadata.user_status;
    const loginCallBackUrl = localStorage.getItem("loginCallBackUrl");
    const survey_id = localStorage.getItem("survey_id");
    const currentPath = [
      Routes.USERSETTING,
      Routes.USERSETTINGCONFIRM,
      Routes.SURVEY,
      Routes.SURVEYERR,
      Routes.SURVEY_ANSWER,
      Routes.SURVEY_ANSWER_CONFIRM,
      Routes.SURVEY_ANSWER_FINISH,
      Routes.SURVEY_MEMBER,
      Routes.WITHDRAWAL,
      Routes.WITHDRAWALCONFIRM,
      Routes.WITHDRAWALFINISH,
      Routes.SURVEY
    ];

    const isAllowedPath = currentPath.some((path) => 
      matchPath({ path }, location.pathname)
    );
    const needsRedirect =
      (user_status === 1 || user_status === 3) && !isAllowedPath;
    const returnUrl = localStorage.getItem("returnadvertimes");
    const external = localStorage.getItem("external");
    const hasReturnUrl = !!returnUrl;

    // FIXME:: returntoなかったらアドタイデフォになるのは危険
    let url = returnUrl || "https://www.advertimes.com/";
    
    if (user_status === 4) {
      navigate(Routes.CONFIRMSUCCESS);
    } else if (hasReturnUrl) {
      // 外部ドメインへのリダイレクト処理
      const sessionId = await createExternalSession();
      const urlObj = new URL(url);
      if (sessionId) {
        urlObj.searchParams.set("session_id", sessionId);
      }
      attachBulkDownloadIfNeeded(urlObj);
      url = urlObj.toString();
      localStorage.removeItem("external");
      localStorage.removeItem("returnadvertimes");
      localStorage.removeItem("loginCallBackUrl");
      window.location.href = url;
    } else if (needsRedirect && !loginCallBackUrl && !survey_id) {
      logger("Redirecting to USERSETTING due to user_status:", user_status);
      navigate(Routes.USERSETTING);
    }
  };

  // 互換性のための既存関数（段階的に廃止予定）
  const handleFetchUserInfo = async (user_id: string) => {
    setGlobalState((prevState) => ({
      ...prevState,
      loader: true,
    }));
    try {
      const userData = await fetchAndSetUserInfo();
      if (userData) {
        await handleUserRedirect(userData);
      }
    } catch (err) {
      return err;
    } finally {
      setGlobalState((prevState) => ({
        ...prevState,
        loader: false,
      }));
    }
  };

  const setModal = (modal: CommonModalProps) => {
    setGlobalState((prevState) => ({
      ...prevState,
      modalContext: {
        ...prevState.modalContext,
        ...modal,
      },
    }));
  };

  const setModalDisabledConfirm = (value: boolean) => {
    setGlobalState((prevState) => ({
      ...prevState,
      modalContext: {
        ...prevState.modalContext,
        disabledConfirm: value,
      },
    }));
  };

  const setStateValue = (
    key: string,
    value: string | boolean | IBreadcrumb[] | null
  ) => {
    setGlobalState((prevState) => {
      const newState = { ...prevState, [key]: value };
      return newState;
    });
  };

  const clearStateValue = (key: string) => {
    setGlobalState((prevState) => {
      const newState = { ...prevState, [key]: "" };
      return newState;
    });
  };

  return (
    <GlobalContext.Provider
      value={{
        globalState,
        setGlobalState,
        setStateValue,
        setModal,
        clearStateValue,
        refreshAccessToken,
        createExternalSession,
        fetchAndSetUserInfo,
        handleUserRedirect,
        setModalDisabledConfirm,
      }}
    >
      {children}
    </GlobalContext.Provider>
  );
};

export const useGlobalState = (): GlobalContextType => {
  const context = useContext(GlobalContext);
  if (!context) {
    throw new Error();
  }
  return context;
};
